import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Chip, Grid2 as Grid, Stack, Tooltip, Typography } from '@mui/material';
import { DeviceUnknown, MonitorHeart, Sensors, Watch } from '@mui/icons-material';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import SkeletonEarningCard from '../../../component/cards/Skeleton/EarningCard';
import { SeeButton } from '../../../component/buttons';
import { useRecords } from 'hooks/useRecords';
import { formatDateMoment, getHoursAndMinutes } from 'utils/dateUtils';

const RenderEditCell = ({ params, seeUserState }) => {
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <Grid container justifyContent="center" alignItems="center">
            <SeeButton
                onClick={() =>
                    navigate('record', {
                        state: {
                            record: params.row,
                            from: location,
                            seeUserState
                        }
                    })
                }
                size="small"
            >
                <Typography variant="subtitle2">{t('users.see')}</Typography>
            </SeeButton>
        </Grid>
    );
};

RenderEditCell.propTypes = {
    params: PropTypes.object.isRequired,
    seeUserState: PropTypes.object
};

const getDeviceIcon = (record) => {
    const name = (
        record?.config?.device?.name ||
        record?.config?.name ||
        ''
    ).toLowerCase();
    const dataTypes = record?.config?.dataTypes || [];

    if (name.includes('h10') || dataTypes.includes('ECG')) {
        return <MonitorHeart color="primary" />;
    }

    if (name.includes('360') || name.includes('watch')) {
        return <Watch color="primary" />;
    }

    if (name) {
        return <Sensors color="primary" />;
    }

    return <DeviceUnknown color="disabled" />;
};

function Records({ patient, planUuid = null, seeUserState }) {
    const { t } = useTranslation();
    const {
        data,
        isLoading,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    } = useRecords(patient, null, planUuid, 'EXERCISE');

    const TYPE_LABELS = {
        rehabilitation: t('exercise.types.rehabilitation'),
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other')
    };

    const tableConfig = {
        columns: [
            {
                field: 'ti',
                headerName: t('exerciseTable.date'),
                headerAlign: 'left',
                display: 'flex',
                align: 'left',
                flex: 1,
                minWidth: 140,
                valueGetter: (ti) => formatDateMoment(ti)
            },
            {
                field: 'hora',
                headerName: t('exerciseTable.time'),
                headerAlign: 'left',
                display: 'flex',
                align: 'left',
                flex: 1,
                minWidth: 140,
                renderCell: (params) => getHoursAndMinutes(params.row?.ti || '')
            },
            {
                field: 'config',
                headerName: t('exerciseTable.type'),
                minWidth: 140,
                flex: 1,
                valueGetter: (config) =>
                    TYPE_LABELS[config?.exerciseType] ||
                    config?.exerciseType ||
                    'Sem exercicio'
            },
            {
                field: 'device',
                headerName: t('records.device'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                flex: 1,
                minWidth: 200,
                sortable: false,
                valueGetter: (_value, row) =>
                    row?.config?.device?.name ||
                    row?.config?.name ||
                    t('records.deviceUnavailable'),
                renderCell: (params) => {
                    const config = params.row?.config;
                    const name =
                        config?.device?.name ||
                        config?.name ||
                        t('records.deviceUnavailable');
                    const id = config?.device?.id || config?.id || '-';
                    const firmware =
                        config?.device?.firmwareVersion || config?.firmwareVersion || '-';
                    return (
                        <Box sx={{ width: '100%', textAlign: 'center' }}>
                            <Tooltip
                                describeChild
                                title={
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {name}
                                        </Typography>
                                        <Typography variant="caption" component="div">
                                            ID: {id}
                                        </Typography>
                                        <Typography variant="caption" component="div">
                                            {t('records.firmwareVersion')}: {firmware}
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Box
                                    tabIndex={params.hasFocus ? 0 : -1}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                        width: '100%'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                        {getDeviceIcon(params.row)}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ minWidth: 0 }}
                                    >
                                        {name}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Box>
                    );
                }
            },
            {
                field: 'dataTypes',
                headerName: 'Dados',
                flex: 1,
                minWidth: 140,
                sortable: false,
                renderCell: (params) => {
                    const dataTypes = params.row?.config?.dataTypes || [];
                    const visibleDataTypes = dataTypes.slice(0, 3);
                    const hiddenCount = Math.max(
                        0,
                        dataTypes.length - visibleDataTypes.length
                    );

                    if (!dataTypes.length) {
                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%'
                                }}
                            >
                                <Chip size="small" variant="outlined" label="Sem dados" />
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%'
                            }}
                        >
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {visibleDataTypes.map((type) => (
                                    <Chip
                                        key={`${params.row.uuid}-${type}`}
                                        size="small"
                                        variant="outlined"
                                        label={type}
                                    />
                                ))}
                                {hiddenCount > 0 && (
                                    <Tooltip title={dataTypes.slice(3).join(', ')}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`+${hiddenCount}`}
                                        />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                    );
                }
            },
            {
                field: 'duration',
                headerAlign: 'center',
                headerName: t('exerciseTable.duration'),
                display: 'flex',
                align: 'center',
                flex: 1,
                minWidth: 140,
                valueGetter: (duration) =>
                    duration != null && Number(duration) > 0
                        ? `${Math.round(duration / 60)} min`
                        : 'Sem dados'
            },
            {
                field: 'edit',
                headerName: t('exerciseTable.actions'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                flex: 1,
                minWidth: 140,
                renderCell: (params) => (
                    <RenderEditCell params={params} seeUserState={seeUserState} />
                )
            }
        ]
    };

    return (
        <div>
            {isLoading ? (
                <SkeletonEarningCard />
            ) : (
                <DataGrid
                    paginationMode="server"
                    style={{ width: '100%' }}
                    density="standard"
                    rowHeight={56}
                    loading={isLoading}
                    rows={data}
                    getRowId={(row) => row.uuid}
                    columns={tableConfig.columns}
                    pageSizeOptions={rowsPerPageOptions}
                    rowCount={count}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sx={{
                        '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                            px: 1.5
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 700
                        }
                    }}
                />
            )}
        </div>
    );
}

Records.propTypes = {
    patient: PropTypes.string,
    planUuid: PropTypes.string,
    seeUserState: PropTypes.object
};

export default Records;
